import React, { type ReactElement } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import type { GradingData } from '../services/transcriptionService';

interface GradingPanelProps {
  gradingData: GradingData | null;
  enforcementMode?: string;
}

const statusConfig: Record<string, { color: 'success' | 'warning' | 'error' | 'info'; icon: ReactElement; label: string }> = {
  VALIDATED: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Validated' },
  CONFLICT: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Conflict' },
  HALLUCINATION: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Hallucination' },
  INSUFFICIENT_FEATURES: { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Insufficient Features' },
};

const confidenceConfig: Record<string, { color: 'success' | 'warning' | 'default' }> = {
  high: { color: 'success' },
  medium: { color: 'warning' },
  low: { color: 'default' },
};

const GradingPanel: React.FC<GradingPanelProps> = ({ gradingData, enforcementMode }) => {
  if (!gradingData || !gradingData.enabled) {
    return null;
  }

  const { kept_grades, removed_grades, flags, applicable_systems, extracted_count } = gradingData;
  const hasRemoved = removed_grades.length > 0;
  const hasFlags = flags.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Structured Grading
        </Typography>
        <Chip
          label={`${kept_grades.length} of ${extracted_count} kept`}
          size="small"
          color={hasRemoved ? 'warning' : 'success'}
          variant="outlined"
        />
        {enforcementMode && (
          <Chip
            label={`Mode: ${enforcementMode}`}
            size="small"
            variant="outlined"
            color="info"
          />
        )}
      </Box>

      {applicable_systems.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {applicable_systems.map((sys: string) => (
            <Chip key={sys} label={sys} size="small" variant="outlined" color="info" />
          ))}
        </Box>
      )}

      {hasFlags && (
        <Alert severity="warning" sx={{ mb: 1.5 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            {flags.length} grading flag{flags.length > 1 ? 's' : ''} detected. Review before finalizing.
          </Typography>
        </Alert>
      )}

      {/* Kept grades */}
      {kept_grades.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Confirmed Grades
          </Typography>
          {kept_grades.map((grade: GradingData['kept_grades'][number], idx: number) => {
            const status = statusConfig[grade.status] || statusConfig.VALIDATED;
            const conf = confidenceConfig[grade.confidence] || confidenceConfig.medium;
            return (
              <Tooltip key={idx} title={grade.reason || grade.grade_label} arrow>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    mb: 0.5,
                  }}
                >
                  <Chip
                    icon={status.icon}
                    label={status.label}
                    size="small"
                    color={status.color}
                    variant="outlined"
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {grade.grading_system}
                  </Typography>
                  <Chip
                    label={`Grade ${grade.grade}`}
                    size="small"
                    color="primary"
                    variant="filled"
                  />
                  <Chip
                    label={grade.confidence}
                    size="small"
                    color={conf.color}
                    variant="outlined"
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}

      {/* Removed grades */}
      {removed_grades.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Removed / Blocked Grades
          </Typography>
          {removed_grades.map((grade: GradingData['removed_grades'][number], idx: number) => {
            const status = statusConfig[grade.status] || statusConfig.INSUFFICIENT_FEATURES;
            return (
              <Tooltip key={idx} title={grade.reason} arrow>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                    backgroundColor: 'error.lighter',
                    mb: 0.5,
                    opacity: 0.9,
                  }}
                >
                  <Chip
                    icon={status.icon}
                    label={status.label}
                    size="small"
                    color={status.color}
                    variant="outlined"
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {grade.grading_system}
                  </Typography>
                  <Chip
                    label={`Grade ${grade.grade}`}
                    size="small"
                    color="default"
                    variant="filled"
                  />
                  <InfoIcon fontSize="small" color="disabled" />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default GradingPanel;
